import { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-region-progress-report.css';
import DashboardPageFrame from '../components/DashboardPageFrame.jsx';
import ProgressReportEditPopup from '../components/ProgressReportEditPopup.jsx';
import {
  getProgressReports,
  updateProgressReport,
  getRisks,
  getProblems,
  getActors,
  getEmployees,
  getScheduleOemRegionPairs,
} from '../api/dashboardApi';
import { DASHBOARD_TABS } from '../constants/tabs';
import { buildLookupMap, buildOptionsByOemRegion, resolveEmployeeIds, resolveIdsByName } from '../utils/converters.js';
import { displayRow } from '../utils/regionProgressReportConverters.js';
import { normalizeProgressStatusIcon } from '../utils/progressReportConverters.js';

function ChevronIcon() {
  return (
    <svg className="rr-select-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FilterClearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 4 }}>
      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const POPUP_COLUMNS = [
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', required: true },
  { key: 'reportDate', label: '報告日', type: 'date', required: true },
  { key: 'region', label: '領域', type: 'select', required: true },
  { key: 'assignee', label: '担当者', type: 'tags', required: true },
  { key: 'progressStatus', label: '進捗状況', type: 'select', required: true },
  { key: 'comment', label: '進捗コメント', type: 'text', required: true },
  { key: 'riskTitle', label: '関連リスク', type: 'tags' },
  { key: 'problemTitle', label: '関連課題', type: 'tags' },
  { key: 'pmoComment', label: 'PMOコメント', type: 'text' },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
];


export default function DashboardRegionProgressReportPage() {
  const [rawReports, setRawReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [oemFilter, setOemFilter] = useState('すべて');
  const [dateFilter, setDateFilter] = useState('すべて');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Master data
  const [riskOptionsByOemRegion, setRiskOptionsByOemRegion] = useState({});
  const [problemOptionsByOemRegion, setProblemOptionsByOemRegion] = useState({});
  const [actorOptions, setActorOptions] = useState(['']);
  const [employeeOptions, setEmployeeOptions] = useState(['']);
  const [oemRegionOptions, setOemRegionOptions] = useState(['']);
  const [riskByTitle, setRiskByTitle] = useState(new Map());
  const [problemByTitle, setProblemByTitle] = useState(new Map());
  const [actorByName, setActorByName] = useState(new Map());
  const [employeeByName, setEmployeeByName] = useState(new Map());
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [data, riskData, problemData, actorData, oemRegionPairs, employeeData] =
          await Promise.all([
            getProgressReports(),
            getRisks(),
            getProblems(),
            getActors(),
            getScheduleOemRegionPairs(),
            getEmployees(),
          ]);
        if (!active) return;

        setRawReports(data);

        // 関連リスク・関連課題の候補は行のOEM×対地に限定する（別OEM×対地への誤リンク防止）
        setRiskOptionsByOemRegion(buildOptionsByOemRegion(riskData, [(i) => i.title, (i) => i.noId, (i) => i.name]));
        setProblemOptionsByOemRegion(buildOptionsByOemRegion(problemData, [(i) => i.summary, (i) => i.title, (i) => i.noId, (i) => i.name]));
        setActorByName(buildLookupMap(actorData, 'actor'));
        setActorOptions(['', ...actorData.map((i) => i.actor).filter(Boolean)]);
        setEmployeeByName(buildLookupMap(employeeData, 'user_name', 'employee_id'));
        setEmployeeOptions(['', ...employeeData.map((i) => i.user_name).filter(Boolean)]);

        const riskTitleMap = new Map();
        riskData.forEach((item) => {
          const key = item.title || item.noId || item.name || String(item.id);
          /* istanbul ignore else -- 最後のフォールバックが String(item.id) なので key は必ず真値 */
          if (key) riskTitleMap.set(key, item.id);
        });
        setRiskByTitle(riskTitleMap);

        const problemTitleMap = new Map();
        problemData.forEach((item) => {
          const key = item.summary || item.title || item.noId || item.name || String(item.id);
          /* istanbul ignore else -- 同上 */
          if (key) problemTitleMap.set(key, item.id);
        });
        setProblemByTitle(problemTitleMap);

        const orOpts = [''];
        const orMap = new Map();
        oemRegionPairs.forEach((p) => {
          if (!orMap.has(p.label)) {
            orOpts.push(p.label);
            orMap.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
          }
        });
        setOemRegionOptions(orOpts);
        setOemRegionLookup(orMap);
      } catch {
        if (!active) return;
        setError('進捗報告データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, []);

  const displayRows = useMemo(() => rawReports.map(displayRow), [rawReports]);

  const oemOptions = useMemo(() => ['すべて', ...Array.from(new Set(displayRows.map((r) => r.oemRegion)))], [displayRows]);
  const dateOptions = useMemo(() => ['すべて', ...Array.from(new Set(displayRows.map((r) => r.reportDate)))], [displayRows]);

  const filteredIndices = useMemo(
    () =>
      displayRows.reduce((acc, row, i) => {
        if (
          (oemFilter === 'すべて' || row.oemRegion === oemFilter) &&
          (dateFilter === 'すべて' || row.reportDate === dateFilter)
        ) {
          acc.push(i);
        }
        return acc;
      }, []),
    [displayRows, oemFilter, dateFilter],
  );

  const popupColumns = useMemo(() => {
    const oemKey = selectedRow?.oemRegion || '';
    // 既存の入力値（別OEM×対地の旧リンク等）は解除できるよう候補に残す
    const withCurrentValues = (base, value) => {
      const opts = [...(base || [''])];
      String(value || '').split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((v) => { if (!opts.includes(v)) opts.push(v); });
      return opts;
    };
    return POPUP_COLUMNS.map((col) => ({
      ...col,
      options:
        col.key === 'oemRegion' ? oemRegionOptions
        : col.key === 'riskTitle' ? withCurrentValues(riskOptionsByOemRegion[oemKey], selectedRow?.riskTitle)
        : col.key === 'problemTitle' ? withCurrentValues(problemOptionsByOemRegion[oemKey], selectedRow?.problemTitle)
        : col.key === 'region' ? actorOptions
        : col.key === 'assignee' ? employeeOptions
        // 未登録（空）を許さないため空選択肢は置かない。デフォルトは晴れ
        : col.key === 'progressStatus' ? ['☼', '☁', '☂']
        : undefined,
    }));
  }, [actorOptions, employeeOptions, oemRegionOptions, problemOptionsByOemRegion, riskOptionsByOemRegion, selectedRow]);

  const clearFilters = () => {
    setOemFilter('すべて');
    setDateFilter('すべて');
  };

  const handleRowClick = useCallback((index) => {
    setSaveError('');
    const row = rawReports[index];
    setSelectedRow({
      ...row,
      progressStatus: normalizeProgressStatusIcon(row.progressStatus),
    });
  }, [rawReports]);

  const handleSave = useCallback(async (editedRow) => {
    try {
      setSaving(true);
      setSaveError('');

      let oemId = null;
      let regionId = null;
      if (editedRow.oemRegion && oemRegionLookup.has(editedRow.oemRegion)) {
        const resolved = oemRegionLookup.get(editedRow.oemRegion);
        oemId = resolved.oemId;
        regionId = resolved.regionId;
      }

      const eids = resolveEmployeeIds(editedRow.assignee, employeeByName);
      /* istanbul ignore next -- 領域は必須項目でポップアップが空のままの保存を通さない */
      const actorId = editedRow.region ? (actorByName.get(editedRow.region) || null) : null;

      const payload = {
        report_date: editedRow.reportDate,
        title: editedRow.oemRegion || '',
        progress_status: normalizeProgressStatusIcon(editedRow.progressStatus),
        progress_comment: editedRow.comment,
        pmo_comment: editedRow.pmoComment,
        // tags型（カンマ区切り複数）のため、進捗報告タブと同じ複数ID方式で送信する
        risk_ids: resolveIdsByName(editedRow.riskTitle, riskByTitle),
        problem_ids: resolveIdsByName(editedRow.problemTitle, problemByTitle),
        employee_ids: eids,
        updated_at_iso: editedRow.updated_at_iso ?? null,
      };
      if (oemId) payload.oem_id = oemId;
      if (regionId) payload.region_id = regionId;
      if (actorId) payload.actor_id = actorId;

      await updateProgressReport(editedRow.id, payload);

      const data = await getProgressReports();
      setRawReports(data);
      setSelectedRow(null);
    } catch (err) {
      if (err.status === 409) {
        setSaveError('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      } else {
        setSaveError(`保存エラー: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  }, [actorByName, employeeByName, oemRegionLookup, problemByTitle, riskByTitle]);

  return (
    <DashboardPageFrame mainClassName="rr-main" rootClassName="rr-page" secondaryTabActiveIndex={1} secondaryTabs={DASHBOARD_TABS}>
        <section className="rr-panel" data-node-id="1088:60">
          <div className="rr-filter-toggle-wrap">
            <button
              className="rr-filter-toggle"
              onClick={() => setShowFilters((prev) => !prev)}
              type="button"
            >
              {showFilters ? 'フィルターを閉じる' : 'フィルターを開く'}
            </button>
          </div>

          {showFilters && (
          <div className="rr-filters" data-node-id="1089:493">
            <label className="rr-filter-group">
              <span className="rr-filter-label">OEM×対地:</span>
              <div className="rr-select-wrap">
                <select className="rr-select" onChange={(e) => setOemFilter(e.target.value)} value={oemFilter}>
                  {oemOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronIcon />
              </div>
            </label>

            <label className="rr-filter-group">
              <span className="rr-filter-label">報告日:</span>
              <div className="rr-select-wrap">
                <select className="rr-select" onChange={(e) => setDateFilter(e.target.value)} value={dateFilter}>
                  {dateOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronIcon />
              </div>
            </label>

            <button className="rr-clear-button" onClick={clearFilters} type="button">
              <FilterClearIcon />
              フィルタクリア
            </button>
          </div>
          )}

          <p className="rr-count">{filteredIndices.length} / {rawReports.length} 件の報告データを表示中</p>

          <div className="rr-table-wrap">
            <div className="rr-row rr-head">
              <div className="rr-cell">OEM×対地</div>
              <div className="rr-cell">報告日</div>
              <div className="rr-cell">領域</div>
              <div className="rr-cell">担当者</div>
              <div className="rr-cell">進捗コメント</div>
              <div className="rr-cell">関連リスク</div>
              <div className="rr-cell">関連課題</div>
            </div>

            {loading && (
              <div className="rr-row">
                <div className="rr-cell rr-message" style={{ gridColumn: '1 / -1' }}>
                  読み込み中...
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="rr-row">
                <div className="rr-cell rr-message" style={{ gridColumn: '1 / -1' }}>
                  {error}
                </div>
              </div>
            )}

            {!loading && !error && filteredIndices.length === 0 && (
              <div className="rr-row">
                <div className="rr-cell rr-message" style={{ gridColumn: '1 / -1' }}>
                  データがありません
                </div>
              </div>
            )}

            {filteredIndices.map((idx) => {
              const row = displayRows[idx];
              return (
                <div
                  className="rr-row rr-row--clickable"
                  key={`${row.oemRegion}-${row.reportDate}-${idx}`}
                  onClick={() => handleRowClick(idx)}
                >
                  <div className="rr-cell">{row.oemRegion}</div>
                  <div className="rr-cell">{row.reportDate}</div>
                  <div className="rr-cell">{row.region}</div>
                  <div className="rr-cell">{row.assignee}</div>
                  <div className="rr-cell rr-comment">{row.comment}</div>
                  <div className="rr-cell rr-center">{row.riskTitle}</div>
                  <div className="rr-cell rr-center">{row.problemTitle}</div>
                </div>
              );
            })}
          </div>
        </section>

      <ProgressReportEditPopup
        open={Boolean(selectedRow)}
        initialRow={selectedRow}
        columns={popupColumns}
        title="進捗報告詳細"
        onClose={() => setSelectedRow(null)}
        onSave={handleSave}
        saving={saving}
        errorMessage={saveError}
      />
    </DashboardPageFrame>
  );
}
